/**
 * CompletionGuard - Global guard that prevents navigation to wizard when completion is in progress
 * 
 * This component runs on every route change and:
 * 1. Checks if there's an active completion lock (SYNCHRONOUSLY, before any React render)
 * 2. If current route is wizard and lock exists -> redirects to results
 * 3. Logs all guard activations for debugging
 * 
 * CRITICAL FIX: This guard now runs SYNCHRONOUSLY during render using useMemo,
 * not just in useEffect. This catches navigation attempts before they complete.
 * 
 * IMPORTANT: This is a "belt-and-suspenders" safeguard in addition to the wizard's own guard
 */
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// ═══════════════════════════════════════════════════════════════════════════════
// COMPLETION LOCK HELPERS - Pure functions, no React dependencies
// ═══════════════════════════════════════════════════════════════════════════════

const getCompletionLock = (orgId) => {
  try {
    const key = `assessment_completion_lock_${orgId || 'default'}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    // Check if expired
    if (new Date(data.expiresAt) < new Date()) {
      console.log('[COMPLETION_LOCK_EXPIRED]', { key, expiresAt: data.expiresAt });
      sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch (e) {
    console.error('[COMPLETION_LOCK_ERROR] Failed to read lock:', e);
    return null;
  }
};

// Check if any completion lock exists (any org) - CRITICAL: doesn't depend on React context
const hasAnyCompletionLock = () => {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('assessment_completion_lock_')) {
        const data = JSON.parse(sessionStorage.getItem(key));
        // Check if not expired
        if (new Date(data.expiresAt) > new Date()) {
          return data;
        }
      }
    }
  } catch (e) {
    console.error('[COMPLETION_LOCK_ERROR] Failed checking locks:', e);
  }
  return null;
};

// Check global completion flag (even more reliable)
const isCompletionInProgress = () => {
  try {
    const stored = sessionStorage.getItem('mm_assessment_completion_in_progress');
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    if (Date.now() > data.expiresAt) {
      sessionStorage.removeItem('mm_assessment_completion_in_progress');
      return null;
    }
    return data;
  } catch (e) {
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// WIZARD PATH DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

const isWizardPath = (pathname) => {
  return pathname === '/assessments/wizard' || 
         pathname === '/scanner/wizard' ||
         pathname.includes('/assessments/wizard');
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESULTS PATH DETECTION
// ═══════════════════════════════════════════════════════════════════════════════

const isResultsPath = (pathname) => {
  return pathname.includes('/assessments/results') || 
         pathname.includes('/scanner/results');
};

const CompletionGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get org ID from localStorage (doesn't depend on React context loading)
  const cachedOrgId = useMemo(() => {
    return localStorage.getItem('mm_active_org_id') || 'default';
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SYNCHRONOUS CHECK: Run during render, before children are mounted
  // This catches navigation attempts BEFORE React commits the render
  // ═══════════════════════════════════════════════════════════════════════════════
  const redirectTarget = useMemo(() => {
    const currentPath = location.pathname;
    
    // Only check on wizard paths
    if (!isWizardPath(currentPath)) {
      return null;
    }
    
    // Check ALL possible completion indicators (no React context dependency)
    
    // 1. Global completion flag (set before navigation)
    const completionFlag = isCompletionInProgress();
    if (completionFlag) {
      console.log('[GLOBAL_COMPLETION_GUARD] Found completion flag', {
        assessmentId: completionFlag.assessmentId,
        currentPath,
        timestamp: new Date().toISOString()
      });
      return `/assessments/results/${completionFlag.assessmentId}?success=1`;
    }
    
    // 2. Org-specific lock
    const orgLock = getCompletionLock(cachedOrgId);
    if (orgLock) {
      console.log('[GLOBAL_COMPLETION_GUARD] Found org-specific lock', {
        assessmentId: orgLock.assessmentId,
        currentPath,
        orgId: cachedOrgId,
        timestamp: new Date().toISOString()
      });
      return `/assessments/results/${orgLock.assessmentId}?success=1`;
    }
    
    // 3. Any lock (fallback)
    const anyLock = hasAnyCompletionLock();
    if (anyLock) {
      console.log('[GLOBAL_COMPLETION_GUARD] Found any-org lock', {
        assessmentId: anyLock.assessmentId,
        currentPath,
        timestamp: new Date().toISOString()
      });
      return `/assessments/results/${anyLock.assessmentId}?success=1`;
    }
    
    return null;
  }, [location.pathname, cachedOrgId]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EFFECT: Perform the actual redirect (navigate can't be called during render)
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (redirectTarget) {
      console.log('[GLOBAL_COMPLETION_GUARD] Executing redirect to:', redirectTarget);
      navigate(redirectTarget, { replace: true });
    }
  }, [redirectTarget, navigate]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // GLOBAL WINDOW FLAG: Set a window-level flag to block ANY reload attempt
  // This survives React remounts because it's on the window object
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const currentPath = location.pathname;
    const hasLock = hasAnyCompletionLock() || isCompletionInProgress();
    
    // Set global flag on results page with completion lock
    if (isResultsPath(currentPath) && hasLock) {
      window.__MM_COMPLETION_IN_PROGRESS = true;
      console.log('[GLOBAL_COMPLETION_GUARD] Window flag SET');
    }
    
    // Clean up when leaving results page (only if explicitly navigating away)
    return () => {
      // Don't clear the flag on unmount - let the user action handlers clear it
      // This prevents clearing during React remounts
    };
  }, [location.pathname]);
  
  // If there's a pending redirect on wizard path, show loading state
  // This prevents the wizard from flashing before redirect
  if (redirectTarget && isWizardPath(location.pathname)) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#6366F1]/20 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6366F1]"></div>
          </div>
          <p className="text-[#A0AEC0]">Loading your results...</p>
        </div>
      </div>
    );
  }
  
  return children;
};

export default CompletionGuard;
